import { z } from 'zod';

/** Alle Rechte der Anlage. `*` (nur Admin) erlaubt alles. */
export const PERMISSIONS = [
  'system.view',
  'users.manage',
  'extensions.manage',
  'trunks.manage',
  'callflows.manage',
  'queues.manage',
  'queues.supervise',
  'schedules.manage',
  'media.manage',
  'contacts.manage',
  'messages.manage',
  'reports.view',
  'integrations.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];
export type PermissionGrant = Permission | '*';

export interface DefaultRole {
  name: string;
  label: string;
  permissions: PermissionGrant[];
}

export const DEFAULT_ROLES: readonly DefaultRole[] = [
  { name: 'admin', label: 'Administrator', permissions: ['*'] },
  {
    name: 'supervisor',
    label: 'Supervisor',
    permissions: ['system.view', 'queues.supervise', 'reports.view', 'contacts.manage', 'messages.manage'],
  },
  { name: 'agent', label: 'Mitarbeiter', permissions: ['contacts.manage', 'messages.manage'] },
  { name: 'readonly', label: 'Nur lesen', permissions: ['reports.view'] },
];

export function hasPermission(granted: readonly string[], needed: Permission): boolean {
  return granted.includes('*') || granted.includes(needed);
}

export const THEMES = ['system', 'dark', 'light'] as const;
export type Theme = (typeof THEMES)[number];

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(1).max(256),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const updateProfileSchema = z.object({
  theme: z.enum(THEMES),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: { name: string; label: string };
  permissions: string[];
  theme: Theme;
}

export type ComponentState = 'ok' | 'error';

export interface ComponentStatus {
  id: string;
  label: string;
  state: ComponentState;
  /** Kurzer, für Menschen lesbarer Zusatz (Version, Fehlerursache). */
  detail?: string;
}

/** Wächst mit den Phasen: später kommen SIP-Server, Medien und easybell-Anmeldung dazu. */
export interface SystemStatus {
  checkedAt: string;
  components: ComponentStatus[];
}

export interface ApiError {
  error: string;
  message: string;
}
